import { NotificationService } from "@/gen/omnios/v1/notification_connect";
import { createClient } from "@connectrpc/connect";
import { create } from "zustand";
import { transport } from "./grpc";

const client = createClient(NotificationService, transport);

interface NotificationItem {
	id: string;
	title: string;
	body: string;
	link: string;
	isRead: boolean;
	createdAt: Date;
}

interface NotificationState {
	items: NotificationItem[];
	unreadCount: number;
	loading: boolean;
	fetchUnreadCount: () => Promise<void>;
	fetchNotifications: () => Promise<void>;
	markRead: (ids: string[]) => Promise<void>;
	markAllRead: () => Promise<void>;
}

export const useNotificationStore = create<NotificationState>()((set) => ({
	items: [],
	unreadCount: 0,
	loading: false,

	fetchUnreadCount: async () => {
		try {
			const res = await client.getUnreadCount({});
			set({ unreadCount: res.count });
		} catch {
			/* skip */
		}
	},

	fetchNotifications: async () => {
		set({ loading: true });
		try {
			const res = await client.listNotifications({
				page: {
					pageSize: 50,
					pageToken: "",
				},
			});
			set({
				items: res.notifications.map((n) => ({
					id: n.id,
					title: n.title,
					body: n.body,
					link: n.link,
					isRead: n.isRead,
					createdAt: n.createdAt ? n.createdAt.toDate() : new Date(),
				})),
			});
		} catch {
			/* skip */
		} finally {
			set({ loading: false });
		}
	},

	markRead: async (ids) => {
		await client.markRead({ ids });
		set((s) => ({
			items: s.items.map((n) =>
				ids.includes(n.id) ? { ...n, isRead: true } : n,
			),
			unreadCount: Math.max(0, s.unreadCount - ids.length),
		}));
	},

	markAllRead: async () => {
		await client.markAllRead({});
		set((s) => ({
			items: s.items.map((n) => ({
				...n,
				isRead: true,
			})),
			unreadCount: 0,
		}));
	},
}));
